import { Head, Link, router, usePage } from "@inertiajs/react"
import { Building2, ChevronRight, LayoutGrid, MapPin, RefreshCw } from "lucide-react"

import PropertyMap from "@/components/broker/property-map"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import AppLayout from "@/layouts/app-layout"
import { brokerPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Property } from "@/types"
import { useState } from "react"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Properties", href: brokerPropertiesPath() },
]

type View = "grid" | "map"

interface Props {
  properties: Property[]
}

export default function Index({ properties }: Props) {
  const [query, setQuery] = useState("")
  const [view, setView] = useState<View>("grid")
  const [geocoding, setGeocoding] = useState(false)

  const isAdmin = (usePage().props.auth as any)?.user?.role?.name === "admin"

  const handleGeocode = () => {
    setGeocoding(true)
    router.post(
      "/admin/properties/geocode",
      {},
      {
        preserveScroll: true,
        onFinish: () => setGeocoding(false),
      },
    )
  }

  const filtered = properties.filter((p) =>
    [p.name, p.city, p.state, p.address]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase())
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Properties" />

      <div className="space-y-6 p-6">
        <div>
          <h1 className="text-2xl font-semibold">Properties</h1>
          <p className="text-muted-foreground text-sm">Browse available suites across our portfolio.</p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Input
            placeholder="Search by name, city, or address..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-sm"
          />

          <div className="flex items-center gap-2">
            {isAdmin ? (
              <Button variant="outline" size="sm" onClick={handleGeocode} disabled={geocoding}>
                <RefreshCw className={`h-4 w-4 ${geocoding ? "animate-spin" : ""}`} />
                Geocode addresses
              </Button>
            ) : null}

            <ToggleGroup
              value={[view]}
              onValueChange={(value) => {
                const next = value[0] as View | undefined
                if (next) setView(next)
              }}
              variant="outline"
            >
              <ToggleGroupItem value="grid" aria-label="List view">
                <LayoutGrid className="h-4 w-4" />
                List
              </ToggleGroupItem>
              <ToggleGroupItem value="map" aria-label="Map view">
                <MapPin className="h-4 w-4" />
                Map
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        {view === "map" ? (
          <PropertyMap properties={filtered} />
        ) : filtered.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Building2 />
              </EmptyMedia>
              <EmptyTitle>No properties found</EmptyTitle>
              <EmptyDescription>Try a different search term.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((property) => (
              <Link key={property.id} href={`/broker/properties/${property.id}`}>
                <Card className="hover:shadow-md cursor-pointer transition-shadow h-full">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base">{property.name}</CardTitle>
                      {(property.available_count ?? 0) > 0 ? (
                        <Badge>{property.available_count} available</Badge>
                      ) : (
                        <Badge variant="secondary">No availability</Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {property.city}, {property.state}
                    </p>
                    <p className="text-muted-foreground text-xs">{property.address}</p>
                  </CardHeader>
                  <CardContent className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm">
                      {property.suites_count} suite{property.suites_count !== 1 ? "s" : ""}
                    </span>
                    <ChevronRight className="text-muted-foreground h-4 w-4" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}
